import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { TrendUp, TrendDown, Target, Moon } from "@phosphor-icons/react";

/**
 * WeeklyScore — surfaces the client's Lazy Lean Weekly Score (/49) prominently
 * on their dashboard. Shows current score, status band (Locked In / On Track /
 * Inconsistent / Reset Required), week-over-week delta, weakest habit, and a
 * weekly sleep total when the Sleep habit is in the plan.
 *
 * Score formula: 7 habits × 7 days = 49 max. Status bands:
 *  • 44–49 = 🟢 Locked In
 *  • 37–43 = 🟡 On Track
 *  • 30–36 = 🟠 Inconsistent
 *  • 0–29  = 🔴 Reset Required
 */
const BAND_COLOR = {
  locked_in: { bar: "#22C55E", border: "#22C55E", glow: "rgba(34,197,94,0.15)" },
  on_track: { bar: "#EAB308", border: "#EAB308", glow: "rgba(234,179,8,0.15)" },
  inconsistent: { bar: "#F97316", border: "#F97316", glow: "rgba(249,115,22,0.15)" },
  reset: { bar: "#FF3333", border: "#FF3333", glow: "rgba(255,51,51,0.15)" },
};

export default function WeeklyScore() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await http.get("/client/weekly-score");
        if (mounted) setData(data);
      } catch (e) { if (mounted) setErr(apiError(e)); }
    })();
    return () => { mounted = false; };
  }, []);

  if (err) {
    return (
      <div className="border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-xs text-[#FF3333]" data-testid="weekly-score-error">
        {err}
      </div>
    );
  }
  if (!data) return null;

  const { current, delta, history } = data;
  const score = current.score;
  const max = current.max || 49;
  const band = current.band;
  const pct = (score / max) * 100;
  const colors = BAND_COLOR[band.key] || BAND_COLOR.reset;
  const sleep = current.sleep;

  return (
    <div
      className="id-card"
      data-testid="weekly-score-card"
      style={{ borderColor: colors.border, boxShadow: `inset 0 0 40px ${colors.glow}` }}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="id-label mb-1 text-[#FF3333]">— Lazy Lean Weekly Score —</div>
          <div className="flex items-baseline gap-2">
            <span
              className="font-oswald text-5xl sm:text-6xl font-bold leading-none"
              style={{ color: colors.bar }}
              data-testid="weekly-score-value"
            >
              {score}
            </span>
            <span className="font-oswald text-2xl text-zinc-500 leading-none">/ {max}</span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-2xl leading-none">{band.emoji}</span>
            <span
              className="font-oswald text-base uppercase tracking-wide font-bold"
              style={{ color: colors.bar }}
              data-testid="weekly-score-band"
            >
              {band.label}
            </span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="id-label text-zinc-500 mb-1">vs Last Week</div>
          <div className="flex items-center justify-end gap-1 font-oswald text-2xl" data-testid="weekly-score-delta">
            {delta > 0 && <TrendUp size={20} weight="bold" className="text-[#22C55E]" />}
            {delta < 0 && <TrendDown size={20} weight="bold" className="text-[#FF3333]" />}
            <span className={delta > 0 ? "text-[#22C55E]" : delta < 0 ? "text-[#FF3333]" : "text-zinc-400"}>
              {delta > 0 ? "+" : ""}{delta}
            </span>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-[#1A1A1A] mb-3 overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: colors.bar }}
          data-testid="weekly-score-bar"
        />
      </div>

      {/* Band copy */}
      <p className="text-zinc-300 text-sm font-manrope leading-relaxed mb-4" data-testid="weekly-score-copy">
        {band.copy}
      </p>

      {/* Weekly sleep total */}
      {sleep && (
        <div className="flex items-start gap-3 px-3 py-2 border border-[#262626] bg-[#0F0F0F] mb-3" data-testid="weekly-score-sleep">
          <Moon size={16} weight="fill" className="text-indigo-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="id-label text-zinc-500 mb-0.5">Sleep this week</div>
            <div className="font-manrope text-sm text-white">
              <span data-testid="weekly-score-sleep-total">{sleep.total_hours}h</span> total
              <span className="text-zinc-500"> · </span>
              <span data-testid="weekly-score-sleep-avg">{sleep.avg_hours}h</span> avg
              <span className="text-zinc-500"> · </span>
              <span data-testid="weekly-score-sleep-nights">{sleep.nights_logged}</span>/7 nights logged
            </div>
          </div>
        </div>
      )}

      {/* Weakest habit */}
      {current.weakest && current.weakest.score < 7 && (
        <div className="flex items-start gap-3 px-3 py-2 border border-[#262626] bg-[#0F0F0F]" data-testid="weekly-score-weakest">
          <Target size={16} weight="bold" className="text-[#FF3333] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="id-label text-zinc-500 mb-0.5">Weakest habit this week</div>
            <div className="font-manrope text-sm text-white truncate">
              {current.weakest.label} <span className="text-zinc-500">— {current.weakest.score}/7</span>
            </div>
          </div>
        </div>
      )}

      {/* 8-week mini-sparkline */}
      {history && history.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[#262626]">
          <div className="id-label mb-2 text-zinc-500">Last 8 weeks</div>
          <div className="flex items-end gap-1 h-12" data-testid="weekly-score-history">
            {history.map((w, i) => {
              const h = Math.max(2, (w.score / max) * 100);
              const c = BAND_COLOR[w.band] || BAND_COLOR.reset;
              return (
                <div
                  key={i}
                  className="flex-1 transition-all"
                  style={{ height: `${h}%`, backgroundColor: c.bar, opacity: i === history.length - 1 ? 1 : 0.55 }}
                  title={`Week of ${w.week_start} · ${w.score}/${max}`}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
